import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

/**
 * Offline Mode Indicator Component
 * Shows connection status and pending sync items
 */
const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingTours, setPendingTours] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Check online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Try to sync when back online
      triggerSync();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Listen for sync messages from service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'TOUR_SYNCED') {
          loadPendingTours();
          setLastSyncTime(new Date().toISOString());
        }
      });
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Load pending tours from IndexedDB
  useEffect(() => {
    loadPendingTours();
  }, []);
  
  const loadPendingTours = async () => {
    try {
      const request = indexedDB.open('gembacoach-offline', 1);
      request.onsuccess = () => {
        const db = request.result;
        if (db.objectStoreNames.contains('pending-tours')) {
          const tx = db.transaction('pending-tours', 'readonly');
          const store = tx.objectStore('pending-tours');
          const getAllRequest = store.getAll();
          getAllRequest.onsuccess = () => {
            setPendingTours(getAllRequest.result || []);
          };
        }
      };
    } catch (error) {
      console.log('Could not load pending tours:', error);
    }
  };
  
  const triggerSync = async () => {
    if ('serviceWorker' in navigator && 'sync' in window.SyncManager) {
      try {
        const registration = await navigator.serviceWorker.ready;
        setIsSyncing(true);
        await registration.sync.register('sync-tours');
        setTimeout(() => {
          setIsSyncing(false);
          loadPendingTours();
        }, 2000);
      } catch (error) {
        console.log('Sync registration failed:', error);
        setIsSyncing(false);
      }
    }
  };
  
  // Don't show anything if online and no pending items
  if (isOnline && pendingTours.length === 0) {
    return null;
  }
  
  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-amber-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 mb-2 animate-pulse">
          <WifiOff className="w-5 h-5" />
          <div>
            <p className="font-medium">Mode hors ligne</p>
            <p className="text-xs opacity-90">Les données affichées peuvent être obsolètes</p>
          </div>
        </div>
      )}
      
      {/* Pending Tours Indicator */}
      {pendingTours.length > 0 && (
        <div className={`${isOnline ? 'bg-blue-600' : 'bg-slate-700'} text-white px-4 py-3 rounded-lg shadow-lg`}>
          <div className="flex items-center gap-3">
            {isSyncing ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Clock className="w-5 h-5" />
            )}
            <div className="flex-1">
              <p className="font-medium">
                {pendingTours.length} tournée{pendingTours.length > 1 ? 's' : ''} en attente
              </p>
              <p className="text-xs opacity-90">
                {isSyncing 
                  ? 'Synchronisation en cours...' 
                  : isOnline 
                    ? 'Cliquez pour synchroniser'
                    : 'Sera synchronisée automatiquement'
                }
              </p>
            </div>
            {isOnline && !isSyncing && (
              <button 
                onClick={triggerSync}
                className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm font-medium transition"
              >
                Sync
              </button>
            )}
          </div>
          
          {/* Pending tours list */}
          {pendingTours.length > 0 && pendingTours.length <= 3 && (
            <div className="mt-2 pt-2 border-t border-white/20 space-y-1">
              {pendingTours.map((tour) => (
                <div key={tour.id} className="flex items-center gap-2 text-xs opacity-80">
                  <AlertTriangle className="w-3 h-3" />
                  <span>
                    {new Date(tour.timestamp).toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                    {' - Zone: '}
                    {tour.data?.zone_id?.substring(0, 8) || 'N/A'}...
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Sync Success Toast */}
      {lastSyncTime && (
        <div className="bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg mt-2 flex items-center gap-2 animate-fade-in">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm">Synchronisation réussie</span>
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
